@use '@/styles/variables' as *;

// ─── Keyframes ────────────────────────────────────────────────────────────────

// Fades the video wrapper in as the section enters the viewport and out as it exits.
// Runs on animation-timeline: view() so the full 0–100% range maps to the element
// travelling from just-below-viewport to just-above-viewport.
@keyframes videoSectionReveal {
  0%,
  5% {
    opacity: 0;
  }
  20% {
    opacity: 1;
  }
  80% {
    opacity: 1;
  }
  95%,
  100% {
    opacity: 0;
  }
}

// ─── Section ──────────────────────────────────────────────────────────────────

.section {
  min-height: 100vh;
  padding: $section-padding-y $section-padding-x;
  scroll-margin-top: $header-height;
  background-color: var(--background);
}

.sectionWithVideo {
  position: relative;
  overflow: hidden;

  // Lift the container above the absolute video layer.
  .container {
    position: relative;
    z-index: 1;
  }
}

// ─── Video background ─────────────────────────────────────────────────────────

.videoWrapper {
  position: absolute;
  inset: 0;
  z-index: 0;

  @supports (animation-timeline: scroll()) {
    animation: videoSectionReveal linear both;
    animation-timeline: #{'view()'};
  }
}

.video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  filter: brightness(1.075);
}

:global(.dark) .video {
  filter: invert(1);
}

// Gradient overlay — blends the video into the section background at the edges
// so section boundaries don't feel abrupt. Uses --background so it respects
// dark/light mode and the alternating even-section tint.
.overlay {
  position: absolute;
  inset: 0;
}

// Tints the inverted video to match the dark mode background hue.
// mix-blend-mode: color preserves the video's luminance while applying the hue/chroma of the overlay.
:global(.dark) .overlay {
  background: var(--mist);
  opacity: 0.85;
  mix-blend-mode: color;
}

// ─── Container ────────────────────────────────────────────────────────────────

.container {
  max-width: $container-max-width;
  margin: 0 auto;
  width: 100%;
}

// ─── Section header ────────────────────────────────────────────────────────────

.sectionHeader {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem 4rem;
  align-items: end;
  margin-bottom: 2.5rem;
}

.sectionLabel {
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--muted-foreground);
  margin-bottom: 0.5rem;
}

.sectionTagline {
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  font-family: var(--font-display);
  letter-spacing: -0.03em;
  line-height: 1.2;
  color: var(--foreground);
  max-width: 24ch;
  text-wrap: balance;
}

.sectionPerex {
  font-size: clamp(0.95rem, 1.5vw, 1.05rem);
  color: var(--muted-foreground);
  line-height: 1.7;
}

@media (max-width: #{$breakpoint-sm}) {
  .sectionHeader {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
}

// Home hero is full-screen and padding-free so the video covers the entire section.
// position: relative + overflow: hidden come from .sectionWithVideo when video is set.
:global(#home) {
  padding: 0;
  min-height: 100vh;
}

// Skills section hugs its content — the bento strip defines its own height
:global(#skills) {
  min-height: auto;
}

// Contact section centers its content vertically
:global(#contact) {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.section:nth-child(even) {
  background-color: color-mix(in oklch, var(--muted) 35%, var(--background));
}
